import React, { useState, useMemo } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { DownloadSimple, UploadSimple, FileCsv, CheckCircle, Warning, X, MicrosoftExcelLogo } from '@phosphor-icons/react';

// Known task fields. Order = displayed order in the mapping UI.
const TASK_FIELDS = [
  // `task_key` is auto-mapped from the export's "task_key" column. The
  // backend uses (task_key, sprint_id) as the row's natural identity for
  // de-duplication, so re-importing the same JIRA export is a safe no-op.
  // Marked optional because freshly-created tasks in a template CSV won't
  // have one — they'll be auto-assigned by the backend.
  { key: 'task_key',         label: 'Task Key (used for de-dup on re-import)' },
  { key: 'title',            label: 'Title',           required: true },
  { key: 'description',      label: 'Description' },
  { key: 'work_type',        label: 'Work Type (task/story/bug)' },
  // parent_task_key links a `task` row to its Story or Bug. Required ONLY
  // when work_type=task and the task is supposed to be a subtask. Stories
  // and Bugs leave it blank — they are top-level.
  { key: 'parent_task_key',  label: 'Parent (only for work_type=task; key like WORK01-3)' },
  { key: 'priority',         label: 'Priority (low/medium/high/critical)' },
  { key: 'status',           label: 'Status' },
  { key: 'assignee_email',   label: 'Assignee Email' },
  { key: 'sprint_name',      label: 'Sprint Name' },
  { key: 'due_date',         label: 'Due Date (YYYY-MM-DD)' },
  { key: 'tags',             label: 'Tags (comma-separated)' },
];

// Case-insensitive auto-detection of CSV header → task field mapping.
function autoMap(headers, customFieldNames) {
  const map = {};
  const lower = headers.map(h => (h || '').toLowerCase().trim());
  for (const f of TASK_FIELDS) {
    const idx = lower.indexOf(f.key.toLowerCase());
    if (idx >= 0) map[f.key] = headers[idx];
  }
  // Custom fields by exact name match
  for (const name of customFieldNames) {
    const idx = lower.indexOf(name.toLowerCase());
    if (idx >= 0) map[`cf:${name}`] = headers[idx];
  }
  return map;
}

export default function ImportExportTasksDialog({ open, onClose, projectId, project, onImported, activeSprint = null }) {
  const { getOrgHeaders, API } = useAuth();
  const [step, setStep] = useState(1); // 1=upload, 2=preview+map, 3=results
  // CSV or Excel: changes the template/export download URLs AND how we parse
  // an uploaded file. CSV → client-side Papa parse, then we POST rows JSON.
  // Excel → we first POST to /parse-xlsx to get headers+rows, then the user
  // confirms the column mapping in the same wizard as CSV, then we POST
  // mapped rows back through /tasks/import (JSON). This makes the import
  // flow feel identical to CSV — same wizard, same preview, same controls.
  const [format, setFormat] = useState('csv');
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]); // array of objects keyed by header
  const [mapping, setMapping] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // Export scope — only meaningful when the dialog is opened from a Board
  // page that has an active sprint. Lets users choose "Active sprint only"
  // (default from board) vs "All tasks in project".
  const [exportScope, setExportScope] = useState(activeSprint ? 'active_sprint' : 'all');

  const customFieldNames = useMemo(() => (project?.custom_fields || []).map(f => f.name), [project]);

  const reset = () => {
    setStep(1); setFile(null); setHeaders([]); setRawRows([]);
    setMapping({}); setResult(null); setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  // Format-aware template + export downloads. Excel hits the new .xlsx
  // endpoints; CSV uses the original ones (now upgraded with parent_task_key).
  const downloadTemplate = async () => {
    try {
      const isExcel = format === 'excel';
      const path = isExcel ? 'tasks/import-template.xlsx' : 'tasks/import-template';
      const mime = isExcel ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv';
      const ext = isExcel ? 'xlsx' : 'csv';
      const { data } = await axios.get(`${API}/projects/${projectId}/${path}`, { withCredentials: true, headers: getOrgHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: mime }));
      const a = document.createElement('a'); a.href = url; a.download = `${project?.key || 'tasks'}_import_template.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(`Failed to download template${e?.response?.status ? ` (${e.response.status})` : ''}`); }
  };

  const downloadExport = async () => {
    try {
      const isExcel = format === 'excel';
      const path = isExcel ? 'tasks/export.xlsx' : 'tasks/export';
      const mime = isExcel ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv';
      const ext = isExcel ? 'xlsx' : 'csv';
      // Append the scope query so the Board page's "Active sprint only"
      // toggle actually narrows the export server-side.
      const qs = exportScope === 'active_sprint' ? '?scope=active_sprint' : '';
      const { data } = await axios.get(`${API}/projects/${projectId}/${path}${qs}`, { withCredentials: true, headers: getOrgHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: mime }));
      const suffix = exportScope === 'active_sprint' ? '_sprint' : '';
      const a = document.createElement('a'); a.href = url; a.download = `${project?.key || 'tasks'}_export${suffix}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(`Failed to export${e?.response?.status ? ` (${e.response.status})` : ''}`); }
  };

  const handleFilePick = async (f) => {
    if (!f) return;
    setError('');
    setFile(f);
    const isExcelFile = /\.xlsx?$/i.test(f.name);
    if (isExcelFile) {
      // Excel path: POST the file to the parse-xlsx endpoint to get back
      // { headers, rows }. From there the user goes through the EXACT same
      // column-mapping wizard as CSV — the only difference is descriptions
      // arrive pre-resolved as HTML (via the meta sheet) instead of plain text.
      setFormat('excel');
      try {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(
          `${API}/projects/${projectId}/tasks/parse-xlsx`, fd,
          { withCredentials: true, headers: { ...getOrgHeaders(), 'Content-Type': 'multipart/form-data' } }
        );
        const hdrs = Array.isArray(data.headers) ? data.headers : [];
        const rows = Array.isArray(data.rows) ? data.rows : [];
        setHeaders(hdrs);
        setRawRows(rows);
        setMapping(autoMap(hdrs, customFieldNames));
        setStep(2);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to read Excel file');
        setFile(null);
      }
      return;
    }
    setFormat('csv');
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const hdrs = res.meta?.fields || [];
        setHeaders(hdrs);
        setRawRows(res.data || []);
        setMapping(autoMap(hdrs, customFieldNames));
        setStep(2);
      },
      error: (err) => setError('CSV parse failed: ' + err.message),
    });
  };

  const buildPayloadRows = () => {
    return rawRows.map(row => {
      const out = {};
      for (const f of TASK_FIELDS) {
        const src = mapping[f.key];
        if (src && row[src] !== undefined) out[f.key] = row[src];
      }
      for (const name of customFieldNames) {
        const src = mapping[`cf:${name}`];
        if (src && row[src] !== undefined) out[name] = row[src];
      }
      return out;
    });
  };

  const handleImport = async () => {
    setSubmitting(true);
    setError('');
    try {
      // Both CSV and Excel paths now share the same flow: rows are already
      // in memory (CSV → Papa.parse client-side, Excel → /parse-xlsx round-trip),
      // and the user has mapped columns in the same wizard. We POST the
      // mapped rows as JSON to the standard /tasks/import endpoint.
      const rows = buildPayloadRows();
      if (!mapping.title) { setError('Please map the Title column — it is required.'); setSubmitting(false); return; }
      const resp = await axios.post(`${API}/projects/${projectId}/tasks/import`, { rows }, { withCredentials: true, headers: getOrgHeaders() });
      setResult(resp.data);
      setStep(3);
      if (resp.data.created?.length && onImported) onImported();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Import failed');
    } finally { setSubmitting(false); }
  };

  const previewRows = rawRows.slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl p-0 max-h-[90vh] flex flex-col" data-testid="import-export-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-100 flex-shrink-0">
          <DialogTitle className="font-['Outfit'] text-lg font-bold text-zinc-900 tracking-tight">Import / Export tasks</DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-500">
            {step === 1 && 'Download the template, fill it in Excel/Sheets, then upload.'}
            {step === 2 && 'Confirm which columns map to task fields. Auto-detected where possible.'}
            {step === 3 && 'Import complete.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && <div className="mb-3 px-3 py-2 rounded bg-red-50 border border-red-200 text-[12px] text-red-700" data-testid="ie-error">{error}</div>}

          {/* STEP 1 — Upload + quick actions */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Format toggle: CSV (default) or Excel. Same column schema —
                  Excel adds rich-text preservation for the Description cell. */}
              <div data-testid="ie-format-toggle" className="flex items-center gap-1 p-1 rounded-lg bg-zinc-100 w-fit">
                <button type="button" onClick={() => setFormat('csv')} data-testid="ie-format-csv"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${format === 'csv' ? 'bg-white shadow-sm text-zinc-800' : 'text-zinc-500 hover:text-zinc-700'}`}>
                  <FileCsv size={14} weight="bold" /> CSV
                </button>
                <button type="button" onClick={() => setFormat('excel')} data-testid="ie-format-excel"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${format === 'excel' ? 'bg-white shadow-sm text-zinc-800' : 'text-zinc-500 hover:text-zinc-700'}`}>
                  <MicrosoftExcelLogo size={14} weight="bold" /> Excel (.xlsx)
                </button>
              </div>
              {format === 'excel' && (
                <p className="text-[11px] text-zinc-500 -mt-2">
                  Excel preserves rich-text formatting in the Description cell (bold, italics, colours).
                  CSV flattens it to plain text.
                </p>
              )}

              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={downloadTemplate} data-testid="ie-download-template"
                  className="flex items-start gap-3 p-4 rounded-lg border border-zinc-200 bg-white hover:border-blue-400 hover:bg-blue-50/30 text-left transition-all">
                  <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0"><DownloadSimple size={18} weight="bold" /></div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">Download template</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{format === 'excel' ? 'Pre-filled .xlsx with columns + sample row' : 'Pre-filled CSV with columns + sample row'}</p>
                  </div>
                </button>
                <button type="button" onClick={downloadExport} data-testid="ie-download-export"
                  className="flex items-start gap-3 p-4 rounded-lg border border-zinc-200 bg-white hover:border-emerald-400 hover:bg-emerald-50/30 text-left transition-all">
                  <div className="w-9 h-9 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                    {format === 'excel' ? <MicrosoftExcelLogo size={18} weight="bold" /> : <FileCsv size={18} weight="bold" />}
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">Export existing tasks</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {exportScope === 'active_sprint'
                        ? `Active sprint only${activeSprint ? ` (${activeSprint.name})` : ''} · ${format === 'excel' ? 'Excel' : 'CSV'}`
                        : `All tasks of this project · ${format === 'excel' ? 'Excel' : 'CSV'}`}
                    </p>
                  </div>
                </button>
              </div>

              {/* Export scope — only meaningful when an active sprint is in play.
                  Lets users decide whether the download covers just the live
                  sprint or the whole project. Defaults to "active sprint"
                  when launched from the Board page. */}
              {activeSprint && (
                <div data-testid="ie-export-scope" className="flex items-center gap-3 px-3 py-2 rounded-md bg-zinc-50 border border-zinc-200">
                  <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-zinc-500">Export scope</span>
                  <div className="flex items-center gap-1 p-0.5 rounded-md bg-white border border-zinc-200">
                    <button type="button" onClick={() => setExportScope('active_sprint')} data-testid="ie-scope-active"
                      className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${exportScope === 'active_sprint' ? 'bg-emerald-600 text-white' : 'text-zinc-600 hover:text-zinc-800'}`}>
                      Active sprint{activeSprint?.name ? ` · ${activeSprint.name}` : ''}
                    </button>
                    <button type="button" onClick={() => setExportScope('all')} data-testid="ie-scope-all"
                      className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${exportScope === 'all' ? 'bg-zinc-700 text-white' : 'text-zinc-600 hover:text-zinc-800'}`}>
                      All tasks
                    </button>
                  </div>
                </div>
              )}

              <label data-testid="ie-upload-zone"
                className="block border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/20 transition-all">
                <UploadSimple size={26} className="mx-auto text-zinc-400 mb-2" />
                <p className="text-[13px] font-medium text-zinc-700">Click to choose a CSV or Excel file, or drop it here</p>
                <p className="text-[11px] text-zinc-400 mt-1">First row must be column headers. File type is auto-detected.</p>
                <input type="file" accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid="ie-file-input"
                  onChange={(e) => handleFilePick(e.target.files?.[0])} />
              </label>
            </div>
          )}

          {/* STEP 2 — Mapping + preview (now identical for CSV and Excel) */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-bold mb-2">Column mapping</p>
                <p className="text-[11px] text-zinc-500 mb-3">
                  {rawRows.length} rows detected from <span className="font-mono text-zinc-700">{file?.name}</span>
                  {format === 'excel' && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold"><MicrosoftExcelLogo size={10} weight="bold" /> Rich-text preserved</span>}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {TASK_FIELDS.map(f => (
                    <div key={f.key} className="flex items-center gap-2">
                      <span className={`text-[12px] w-40 ${f.required ? 'font-semibold text-zinc-800' : 'text-zinc-600'}`}>{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}</span>
                      <Select value={mapping[f.key] || '__none__'} onValueChange={(v) => setMapping(m => ({ ...m, [f.key]: v === '__none__' ? null : v }))}>
                        <SelectTrigger className="h-8 text-[12px] flex-1" data-testid={`ie-map-${f.key}`}><SelectValue placeholder="— Not mapped —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Not mapped —</SelectItem>
                          {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  {customFieldNames.map(name => (
                    <div key={name} className="flex items-center gap-2">
                      <span className="text-[12px] w-40 text-zinc-600">{name} <span className="text-[10px] text-zinc-400">(custom)</span></span>
                      <Select value={mapping[`cf:${name}`] || '__none__'} onValueChange={(v) => setMapping(m => ({ ...m, [`cf:${name}`]: v === '__none__' ? null : v }))}>
                        <SelectTrigger className="h-8 text-[12px] flex-1" data-testid={`ie-map-cf-${name}`}><SelectValue placeholder="— Not mapped —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Not mapped —</SelectItem>
                          {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview first 5 rows — works for both CSV and Excel now
                  that we have row data for both formats. Description cells
                  may contain raw HTML for Excel rows; we truncate to keep
                  the preview compact. */}
              {previewRows.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-bold mb-2">Preview (first 5 rows)</p>
                  <div className="border border-zinc-200 rounded-md overflow-x-auto" data-testid="ie-preview">
                    <table className="w-full text-[11px]">
                      <thead className="bg-zinc-50">
                        <tr>{headers.map(h => <th key={h} className="px-2 py-1.5 text-left font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap">{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, i) => (
                          <tr key={i} className={i % 2 ? 'bg-zinc-50/50' : ''}>
                            {headers.map(h => <td key={h} className="px-2 py-1.5 text-zinc-700 max-w-[200px] truncate">{String(row[h] || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — Results */}
          {step === 3 && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="ie-result-success">
                <CheckCircle size={22} className="text-emerald-600" weight="fill" />
                <div>
                  <p className="text-[13px] font-semibold text-emerald-800">{result.created?.length || 0} task{(result.created?.length || 0) !== 1 ? 's' : ''} created</p>
                  <p className="text-[11px] text-emerald-600">{result.created?.slice(0, 20).join(', ')}{result.created?.length > 20 ? '…' : ''}</p>
                </div>
              </div>
              {/* Duplicates explicitly skipped (key + sprint already exist) —
                  shown separately from row-validation errors because the
                  user typically EXPECTS these on re-imports and we don't
                  want to alarm them with red. Amber = informational. */}
              {result.skipped?.length > 0 && (
                <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="ie-result-skipped">
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200">
                    <Warning size={16} className="text-amber-600" weight="fill" />
                    <p className="text-[12px] font-semibold text-amber-800">{result.skipped.length} duplicate row{result.skipped.length !== 1 ? 's' : ''} skipped (already exist in this project)</p>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {result.skipped.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-[11px] border-b border-amber-100 last:border-0">
                        <span className="font-mono text-zinc-500 min-w-[60px]">Row {s.row_index + 2}</span>
                        <span className="font-mono text-amber-700 font-semibold">{s.task_key}</span>
                        <span className="text-amber-700">{s.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden" data-testid="ie-result-errors">
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border-b border-red-200">
                    <Warning size={16} className="text-red-600" weight="fill" />
                    <p className="text-[12px] font-semibold text-red-800">{result.errors.length} row{result.errors.length !== 1 ? 's' : ''} skipped</p>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-[11px] border-b border-red-100 last:border-0">
                        <span className="font-mono text-zinc-500 min-w-[60px]">Row {e.row_index + 2}</span>
                        <span className="text-red-700">{e.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="px-6 py-3 border-t border-zinc-100 bg-white flex items-center justify-between flex-shrink-0">
          <div className="text-[11px] text-zinc-400">
            {step === 2 && `${rawRows.length} rows will be imported`}
          </div>
          <div className="flex items-center gap-2">
            {step === 2 && <Button variant="ghost" onClick={() => setStep(1)} data-testid="ie-back" className="h-9 px-3 text-[12px] text-zinc-600">Back</Button>}
            {step === 3 ? (
              <Button onClick={handleClose} data-testid="ie-done" className="h-9 px-5 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold">Done</Button>
            ) : (
              <Button variant="ghost" onClick={handleClose} data-testid="ie-cancel" className="h-9 px-3 text-[12px] text-zinc-600"><X size={12} className="mr-1" /> Cancel</Button>
            )}
            {step === 2 && (
              <Button
                onClick={handleImport}
                disabled={submitting || !rawRows.length || !mapping.title}
                data-testid="ie-import"
                className="h-9 px-5 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold"
              >
                {submitting ? 'Importing…' : `Import ${rawRows.length} tasks`}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
